import jsPDF from 'jspdf';

// Helper: load image URL → base64 data URL
const loadImageAsBase64 = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

export const generateCertificatePDF = async (certificate, t) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  const W = doc.internal.pageSize.getWidth();   // 297
  const H = doc.internal.pageSize.getHeight();  // 210

  // ── Background ──────────────────────────────────────────────────────────
  // Deep forest green left stripe
  doc.setFillColor(17, 78, 46);
  doc.rect(0, 0, 72, H, 'F');

  // Soft off-white main area
  doc.setFillColor(250, 252, 248);
  doc.rect(72, 0, W - 72, H, 'F');

  // Subtle top-right accent stripe
  doc.setFillColor(220, 252, 231);
  doc.rect(72, 0, W - 72, 8, 'F');

  // Bottom accent stripe
  doc.setFillColor(220, 252, 231);
  doc.rect(72, H - 8, W - 72, 8, 'F');

  // ── Left panel – CO₂ Neutral branding ───────────────────────────────────
  const panelCX = 36;

  // Large decorative circle (background)
  doc.setFillColor(22, 101, 62);
  doc.circle(panelCX, 52, 24, 'F');

  // Inner circle
  doc.setFillColor(34, 197, 94);
  doc.circle(panelCX, 52, 18, 'F');

  // Leaf symbol – two overlapping ellipses
  doc.setFillColor(255, 255, 255);
  doc.ellipse(panelCX - 3, 48, 8, 12, 'F');
  doc.setFillColor(34, 197, 94);
  doc.ellipse(panelCX + 3, 56, 8, 12, 'F');

  // "CO₂" text inside circle
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('CO\u2082', panelCX, 51, { align: 'center' });
  doc.setFontSize(7);
  doc.text('NEUTRAL', panelCX, 57, { align: 'center' });

  // Decorative divider line
  doc.setDrawColor(34, 197, 94);
  doc.setLineWidth(0.6);
  doc.line(14, 83, 58, 83);

  // Brand title (vertical area)
  doc.setFontSize(11);
  doc.setTextColor(220, 252, 231);
  doc.setFont('helvetica', 'bold');
  doc.text('CO\u2082 NEUTRAL', panelCX, 94, { align: 'center' });

  doc.setFontSize(8);
  doc.setTextColor(167, 243, 208);
  doc.setFont('helvetica', 'normal');
  doc.text('ZERTIFIZIERUNG', panelCX, 102, { align: 'center' });

  // Divider
  doc.setDrawColor(34, 197, 94);
  doc.line(14, 110, 58, 110);

  // Issued / Expiry block
  doc.setFontSize(7);
  doc.setTextColor(167, 243, 208);
  doc.setFont('helvetica', 'normal');
  doc.text('Ausgestellt am', panelCX, 120, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text(certificate.issuedDate || '—', panelCX, 128, { align: 'center' });

  doc.setFontSize(7);
  doc.setTextColor(167, 243, 208);
  doc.setFont('helvetica', 'normal');
  doc.text('G\u00fcltig bis', panelCX, 140, { align: 'center' });
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text(certificate.expiryDate || '—', panelCX, 148, { align: 'center' });

  // Bottom cert-id on left panel
  doc.setFontSize(6);
  doc.setTextColor(134, 239, 172);
  doc.setFont('helvetica', 'normal');
  const certId = certificate.certificateId || '';
  doc.text('Zertifikat-Nr.', panelCX, 163, { align: 'center' });
  doc.setFontSize(6.5);
  doc.setTextColor(255, 255, 255);
  doc.text(certId, panelCX, 170, { align: 'center', maxWidth: 60 });

  // website bottom
  doc.setFontSize(6);
  doc.setTextColor(134, 239, 172);
  doc.text('www.co2neutralpage.com', panelCX, H - 10, { align: 'center' });

  // ── Right panel content ──────────────────────────────────────────────────
  const rx = 82;   // right panel left edge (with margin)
  const rw = W - rx - 12;

  // ── Company logo (top right) ─────────────────────────────────────────────
  if (certificate.logoUrl) {
    const logoData = await loadImageAsBase64(certificate.logoUrl);
    if (logoData) {
      // Scale logo to fit neatly (max 50 wide, 22 tall)
      const maxLogoW = 52;
      const maxLogoH = 22;
      const tempImg = new Image();
      await new Promise((res) => {
        tempImg.onload = res;
        tempImg.onerror = res;
        tempImg.src = logoData;
      });
      const ratio = tempImg.width / tempImg.height;
      let lw = maxLogoW;
      let lh = lw / ratio;
      if (lh > maxLogoH) { lh = maxLogoH; lw = lh * ratio; }
      const lx = W - lw - 14;
      const ly = 14;
      doc.addImage(logoData, 'PNG', lx, ly, lw, lh);
    }
  }

  // ── Title block ──────────────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setTextColor(22, 101, 62);
  doc.setFont('helvetica', 'bold');
  doc.text('ZERTIFIKAT DER CO\u2082-NEUTRALIT\u00c4T', rx, 22);

  doc.setFontSize(22);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  const companyName = certificate.company || '';
  doc.text(companyName, rx, 36, { maxWidth: rw });

  doc.setFontSize(9);
  doc.setTextColor(75, 85, 99);
  doc.setFont('helvetica', 'normal');
  doc.text(
    'Diese Urkunde best\u00e4tigt, dass das Unternehmen offiziell als CO\u2082-neutral zertifiziert ist.',
    rx, 46, { maxWidth: rw }
  );

  // Thin separator
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.4);
  doc.line(rx, 52, W - 14, 52);

  // ── CO₂ highlight badge ───────────────────────────────────────────────────
  const badgeX = rx;
  const badgeY = 57;
  doc.setFillColor(220, 252, 231);
  doc.roundedRect(badgeX, badgeY, 88, 18, 3, 3, 'F');
  doc.setDrawColor(34, 197, 94);
  doc.setLineWidth(0.8);
  doc.roundedRect(badgeX, badgeY, 88, 18, 3, 3);

  doc.setFontSize(18);
  doc.setTextColor(22, 101, 62);
  doc.setFont('helvetica', 'bold');
  doc.text('12.000 t', badgeX + 44, badgeY + 12, { align: 'center' });

  doc.setFontSize(7);
  doc.setTextColor(22, 163, 74);
  doc.setFont('helvetica', 'normal');
  doc.text('CO\u2082 neutralisiert', badgeX + 44, badgeY + 17.5, { align: 'center' });

  // ── Details grid ──────────────────────────────────────────────────────────
  const gridStartX = rx;
  const gridStartY = 82;
  const colW = (rw / 2) - 4;
  const rowH = 18;

  const fields = [
    { label: 'Unternehmen',       value: certificate.company || '—' },
    { label: 'Website',           value: certificate.website || '—' },
    { label: 'CO\u2082-Ausgleich', value: certificate.carbonOffset || '—' },
    { label: 'Status',            value: certificate.status || 'Aktiv', green: true },
    { label: 'Ausstellungsdatum', value: certificate.issuedDate || '—' },
    { label: 'G\u00fcltig bis',   value: certificate.expiryDate || '—' },
  ];

  fields.forEach((field, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const fx = gridStartX + col * (colW + 8);
    const fy = gridStartY + row * rowH;

    // Field background
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(fx, fy, colW, rowH - 2, 2, 2, 'F');
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.3);
    doc.roundedRect(fx, fy, colW, rowH - 2, 2, 2);

    // Label
    doc.setFontSize(6.5);
    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'normal');
    doc.text(field.label, fx + 4, fy + 6);

    // Value
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    if (field.green) {
      doc.setTextColor(22, 163, 74);
    } else {
      doc.setTextColor(17, 24, 39);
    }
    doc.text(field.value, fx + 4, fy + 13, { maxWidth: colW - 8 });
  });

  // ── Blockchain verified seal ──────────────────────────────────────────────
  const sealY = gridStartY + 3 * rowH + 4;
  doc.setFillColor(239, 246, 255);
  doc.roundedRect(rx, sealY, rw, 12, 2, 2, 'F');
  doc.setDrawColor(147, 197, 253);
  doc.setLineWidth(0.4);
  doc.roundedRect(rx, sealY, rw, 12, 2, 2);

  doc.setFontSize(8);
  doc.setTextColor(29, 78, 216);
  doc.setFont('helvetica', 'bold');
  doc.text('\u26d3  Blockchain-verifiziert \u2022 Manipulationssicher \u2022 Global anerkannt', rx + rw / 2, sealY + 8, { align: 'center' });

  // ── Verification count footer ─────────────────────────────────────────────
  const footerY = H - 16;
  doc.setFontSize(7);
  doc.setTextColor(156, 163, 175);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Dieses Zertifikat wurde ${(certificate.verificationCount || 0).toLocaleString('de-DE')} Mal verifiziert \u2022 co2neutralpage.com/certificate/${certId}`,
    rx + rw / 2, footerY, { align: 'center' }
  );

  doc.setFontSize(6.5);
  doc.text(
    'Dieses Zertifikat wurde mit Blockchain-Technologie ausgestellt und kann jederzeit online auf G\u00fcltigkeit gepr\u00fcft werden.',
    rx + rw / 2, footerY + 5.5, { align: 'center', maxWidth: rw }
  );

  doc.save(`zertifikat-${certId}.pdf`);
};
